import { Flex, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Pill, Text } from '@feathr/components';

import { getMatchTypeLabel } from '../AdGroupEditor/KeywordFields.utils';
import type { IAdGroupKeyword } from '../AdStep.types';

interface IAdGroupKeywordsProps {
  keywords: IAdGroupKeyword[];
  /**
   * Messages for every keyword that has any, keyed by keyword key. Omit where there is
   * nothing to flag — the Review step renders the same list without validating it.
   */
  keywordErrors?: Record<string, string[]>;
  /** Match type is shown for positive keywords only; negative keywords have none. */
  showMatchType?: boolean;
}

/**
 * A read-only chip list of an ad group's keywords, for the collapsed summary.
 *
 * @param props - {@link IAdGroupKeywordsProps}.
 */
function AdGroupKeywords({
  keywords,
  keywordErrors = {},
  showMatchType = false,
}: Readonly<IAdGroupKeywordsProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  if (!keywords.length) {
    return <Text.SmallRegular>{t('None yet.')}</Text.SmallRegular>;
  }

  return (
    <Flex gap={theme.spacing.half} wrap={'wrap'}>
      {keywords.map((keyword) => (
        <Pill
          key={keyword.key}
          showPrefix={false}
          // Kept red here too, so a problem shows without expanding the ad group.
          theme={keywordErrors[keyword.key]?.length ? 'red' : 'neutral'}
          truncate={false}
        >
          {showMatchType
            ? `${keyword.text} · ${getMatchTypeLabel(keyword.matchType, t)}`
            : keyword.text}
        </Pill>
      ))}
    </Flex>
  );
}

export default AdGroupKeywords;
